import { IconButton, useToast } from "@chakra-ui/react";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAPI } from "../api/useAPI";
import { useAPIQuery } from "../api/useAPIQuery";
import { useOnAPISuccess } from "../api/useOnAPISuccess";
import { useToastOnAPIError } from "../api/useToastOnAPIError";
import { LOGGED_IN_HOME_PAGE } from "../config";
import { AppPage } from "../utils/AppPage";
import { isParamNumeric } from "../utils/utils";
import { AccountCard } from "./AccountCard";
import { IoSettings } from "react-icons/io5";
import { NotesList } from "../note/NotesList";
import { Card } from "../utils/Card";

export const AccountPage = () => {
  const { accountId } = useParams();

  const api = useAPI();
  const navigate = useNavigate();
  const toast = useToast();

  const isMe = useMemo(
    () => isParamNumeric(accountId) && api.getAccountId() === Number(accountId),
    [accountId, api]
  );

  // Navigate if invalid
  useEffect(() => {
    if (!isParamNumeric(accountId)) {
      navigate(LOGGED_IN_HOME_PAGE);
    }
  }, [accountId, navigate]);

  /*
   * Load account
   */
  const {
    data: accountGetData,
    state: accountGetState,
    query: accountGetQuery,
    error: accountGetError,
    clearData: accountClearData,
  } = useAPIQuery<"accountGet">({
    apiCall: api.accountGet,
  });

  useToastOnAPIError({
    error: accountGetError,
    title: `Unable to get account`,
  });

  useOnAPISuccess<"accountGet">({
    state: accountGetState,
    data: accountGetData,
    onSuccess: (result) => {
      if (result.accounts.length === 0) {
        toast({
          status: "error",
          title: `Unable to find account`,
          position: "top",
        });
        navigate(LOGGED_IN_HOME_PAGE);
      }
    },
  });

  useEffect(() => {
    if (accountId) {
      accountClearData();
    }
  }, [accountClearData, accountId]);

  useEffect(() => {
    if (
      accountGetState !== "loading" &&
      accountGetData === undefined &&
      isParamNumeric(accountId)
    ) {
      accountGetQuery({
        id: Number(accountId),
      });
    }
  }, [accountGetData, accountGetQuery, accountGetState, accountId]);

  const account = useMemo(
    () => accountGetData?.accounts[0] ?? undefined,
    [accountGetData?.accounts]
  );

  return (
    <AppPage
      title={account ? `${account.fName} ${account.lName}` : undefined}
      headerButton={
        isMe ? (
          <IconButton
            icon={<IoSettings />}
            aria-label="settings"
            onClick={() => navigate("/settings")}
          />
        ) : undefined
      }
    >
      {account ? (
        <>
          <AccountCard
            account={account}
            canEditAvatar={isMe}
            clickable={false}
          />
          <Card
            marginBottom={4}
            fontSize={"large"}
            fontWeight={"bold"}
            padding={4}
          >
            {isMe ? "Your" : account.fName + " " + account.lName + "'s"}{" "}
            Activity
          </Card>
          <NotesList queryParams={{ accountId: account.id }} />
        </>
      ) : null}
    </AppPage>
  );
};
